import axiosInstance from './axiosConfig';
import {
  HeaderKeys,
  IAddPatient,
  IAddUser,
  IGetPatientsQuery,
  IUser,
  ICountUsersResponse,
  IDiaryEntry,
} from '../interfaces';

export interface ApiResponse<T> {
  status: boolean;
  meta?: unknown;
  result: T;
}

export type ApiResult<T> = { result: T };

export interface AuthInitResponse {
  transactionId: string;
  autoStartToken: string;
}

export interface AuthStatusResponse {
  transactionId: string;
  status: 'pending' | 'complete' | 'failed' | 'rejected';
  hintCode?: string;
  qrCode?: string;
  token?: string;
  error?: string;
}

export const getMyUserInfo = async (): Promise<IUser> => {
  const response = await axiosInstance.get<ApiResponse<IUser>>('/users/me');

  return response.data.result;
};

export const sendGetPatientsRequest = (query: IGetPatientsQuery) =>
  axiosInstance.get<ApiResponse<IUser[]>>('users', {
    params: query,
  });

export const sendGetPatientDetailsRequest = (id: string) =>
  axiosInstance.get<ApiResponse<IUser>>(`users/${id}`);

export const sendCountUsersRequest = () =>
  axiosInstance.get<ApiResponse<ICountUsersResponse>>('users/count');

export const sendGetDiaryEntriesRequest = (
  startDate: string,
  endDate: string,
  userId: string
) =>
  axiosInstance.get<ApiResponse<IDiaryEntry[]>>('diary', {
    params: { startDate, endDate, userId },
  });

export const sendGetStatisticsFileRequest = (
  startDate: string,
  endDate: string,
  userId: string
) =>
  axiosInstance.get('diary/report/pdf', {
    params: { startDate, endDate, userId },
    responseType: 'blob',
  });

export const getAutostarttoken = async () =>
  axiosInstance.get<AuthInitResponse>('auth/bankid');

export const getAuthStatus = async (
  transactionId: string,
  onDataUpdate: (data: AuthStatusResponse) => void
): Promise<AuthStatusResponse> => {
  const initialResponse = await axiosInstance.post<AuthStatusResponse>(
    'auth/bankid/check',
    { transactionId },
    { withCredentials: true }
  );

  onDataUpdate(initialResponse.data);

  if (
    initialResponse.data?.status === 'complete' ||
    initialResponse.data?.status === 'rejected'
  ) {
    return initialResponse.data;
  }

  return new Promise((resolve, reject) => {
    const authInterval = setInterval(async () => {
      try {
        const response = await axiosInstance.post<AuthStatusResponse>(
          'auth/bankid/check',
          { transactionId },
          { withCredentials: true }
        );

        onDataUpdate(response.data);

        if (
          response.data?.status === 'complete' ||
          response.data?.status === 'rejected' ||
          response.data?.status === 'failed'
        ) {
          clearInterval(authInterval);
          resolve(response.data);
        }
      } catch (error) {
        clearInterval(authInterval);
        reject(error);
      }
    }, 2000);
  });
};

export const sendAddNewUserRequest = (user: IAddUser) =>
  axiosInstance.post<ApiResponse<{ message: string; user: IUser }>>(
    'users/admin',
    JSON.stringify(user),
    {
      headers: {
        [HeaderKeys.ContentTypeKey]: 'application/json',
      },
    }
  );

export const sendAddNewPatientRequest = (user: IAddPatient) =>
  axiosInstance.post<ApiResponse<{ message: string; user: IUser }>>(
    'users/patient',
    JSON.stringify(user),
    {
      headers: {
        [HeaderKeys.ContentTypeKey]: 'application/json',
      },
    }
  );

export const sendUpdateUserProfileRequest = (
  property: string,
  data: unknown,
  id: string
) =>
  axiosInstance.put<ApiResponse<{ user: IUser }>>(`users/${id}`, {
    [property]: data,
  });

export type ContactField = 'email' | 'phoneNumber';

export const sendUpdatePatientContactRequest = (
  property: ContactField,
  data: string,
  id: string
) =>
  axiosInstance.put<ApiResponse<{ user: IUser }>>(`users/admin/${id}/contact`, {
    [property]: data,
  });

export const sendUpdateUserRequest = (user: Partial<IUser>, id: string) => {
  const formData = new FormData();

  Object.entries(user).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value as string | Blob);
    }
  });

  return axiosInstance.put<ApiResponse<{ user: IUser }>>(
    `users/${id}`,
    formData
  );
};

export const sendAppointmentBookedRequest = (id: string, notify = true) =>
  axiosInstance.put<ApiResponse<{ user: IUser }>>(
    `users/${id}/appointment-booked`,
    { notify }
  );
